import { useState, useEffect } from 'react';
import { discussionToggleLabel, loadDiscussionOpen, saveDiscussionOpen } from './projectLayout';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router';
import { PageHeader } from '../../layouts/Shells';
import {
  Badge,
  Button,
  EmptyState,
  InlineError,
  Input,
  Panel,
  Select,
  Skeleton,
} from '../../components/ui';
import { Row, RowGroup } from '../../components/ui/RowGroup';
import { CopyLinkButton } from '../sharing/CopyLinkButton';
import { projectShareUrl } from '../sharing/shareLinks';
import { RichTextEditor, RichTextView } from '../../components/RichText';
import { useAuth } from '../auth/AuthProvider';
import { ListView } from '../listView/ListView';
import { formatDate } from '../listView/TaskTable';
import { canManageSections } from '../sections/types';
import { FilesSection } from '../files/FilesSection';
import { CommentsSection } from '../comments/CommentsSection';
import { MembersPanel } from './MembersPanel';
import { canEditProject } from './types';
import {
  handOverErrorMessage,
  useArchiveProject,
  useHandOverProject,
  useProjectCategories,
  useProjects,
  useProjectWorkspace,
  useTapPeople,
  useUpdateProject,
} from './hooks';
import {
  PROJECT_WORKFLOW_LABELS,
  PROJECT_WORKFLOW_TONES,
  VISIBILITY_LABELS,
  knownCategories,
  normalizeCategory,
} from './types';
import type { Profile } from '../auth/access';
import type {
  ProjectMemberWithProfile,
  ProjectVisibility,
  ProjectWorkflowStatus,
  ProjectWorkspace,
  TapPerson,
} from './types';

type ProjectTab = 'overview' | 'tasks';

/**
 * Sub-nav for a project (redesign spec). Activity is not a tab yet: the app
 * has no project-wide activity feed to put in one — task history lives in
 * each task's drawer — so shipping an empty tab would be a promise the page
 * cannot keep.
 */
const PROJECT_TABS: { id: ProjectTab; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'tasks', label: 'Tasks' },
];

/**
 * Below this the two columns cannot both be useful.
 *
 * 1024px is where a 35% sidebar stops being a comfortable reading column and
 * starts being a gutter with words in it. Below it the discussion becomes a
 * slide-over, which is also what a tablet expects.
 *
 * The number is duplicated in components.css because a media query cannot read
 * a JS constant. projectLayout.test.ts asserts they agree — if they drift, the
 * button says "open" while CSS has already stacked the panel.
 */
export const SPLIT_BREAKPOINT_PX = 1024;

/**
 * The Category row's last option, "New category…", which opens a text box
 * rather than saving.
 *
 * A sentinel and not an empty string, because '' is already a real choice on
 * that select — it means None. Two spellings of "nothing" on one control is
 * how a project ends up with its category silently cleared by somebody who
 * meant to type a new one. Angle brackets so it cannot collide with a category
 * a person types: normalizeCategory() would fold a plain "new" into an
 * existing "New".
 */
const NEW_CATEGORY = '<new>';

function memberLabel(member: ProjectMemberWithProfile): string {
  return member.display_name.trim() !== '' ? member.display_name : member.email;
}

function ProjectWorkspaceView({
  workspace,
  profile,
  deepLinkTaskId,
  deepLinkCommentId,
}: {
  workspace: ProjectWorkspace;
  profile: Profile | null;
  deepLinkTaskId: string | null;
  deepLinkCommentId: string | null;
}) {
  const { project, members, sections, statuses } = workspace;

  /**
   * Take the deep link out of the URL, now that it has been read.
   *
   * Consumed once: without this, collapsing the discussion and then reloading
   * would reopen it, because the ?comment= that forced it open is still there.
   *
   * It has to happen HERE rather than in ProjectPage. The state below reads
   * the link in a useState initializer, which runs on this component's first
   * render — and this component does not render at all until the project query
   * resolves. Clearing one level up beat that render on any request slower
   * than instant, which is every real one.
   */
  const [, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (deepLinkTaskId !== null || deepLinkCommentId !== null) {
      setSearchParams({}, { replace: true });
    }
  }, [deepLinkTaskId, deepLinkCommentId, setSearchParams]);

  const owner = members.find((member) => member.project_role === 'owner');
  const canManage = profile ? canManageSections(profile, project.owner_id) : false;
  const handOver = useHandOverProject(project.id);
  const [ownerError, setOwnerError] = useState('');
  const people = useTapPeople(true);

  /**
   * Anyone on the TAP team, not only current members: handing a project over
   * usually happens BECAUSE the new owner is not on it yet, and the database
   * adds their membership as part of the transfer.
   */
  const teamOptions = (people.data ?? []).map((person: TapPerson) => ({
    value: person.id,
    label: person.display_name.trim() === '' ? person.email : person.display_name,
  }));

  /**
   * The current owner is always an option, even before the team list loads or
   * if they are somehow not in it. Without this the select has no matching
   * value and renders BLANK — the owner appearing to be nobody, on the one
   * field whose whole job is to say who owns this.
   */
  const ownerOptions = teamOptions.some((option) => option.value === project.owner_id)
    ? teamOptions
    : [
        { value: project.owner_id, label: owner ? memberLabel(owner) : 'Current owner' },
        ...teamOptions,
      ];
  const canEdit = profile ? canEditProject(profile, project.owner_id, members) : false;

  const navigate = useNavigate();
  const projectList = useProjects();
  const curated = useProjectCategories();
  const categories = [...(curated.data ?? [])];
  for (const name of knownCategories(projectList.data ?? [])) {
    if (!categories.some((known) => known.toLocaleLowerCase() === name.toLocaleLowerCase())) {
      categories.push(name);
    }
  }
  categories.sort((left, right) => left.localeCompare(right));

  /**
   * The list the Category row offers, with the project's own category in it
   * whether or not the curated list knows about it.
   *
   * Same reasoning as ownerOptions above: a select whose value matches none of
   * its options renders BLANK. A project categorised before the curated list
   * existed — or one whose category was removed from it since — would show an
   * empty box where its category is, which reads as "no category" rather than
   * as "a category this list has not heard of".
   */
  const categoryOptions =
    project.category.trim() === '' ||
    categories.some((known) => known.toLocaleLowerCase() === project.category.toLocaleLowerCase())
      ? categories
      : [...categories, project.category].sort((left, right) => left.localeCompare(right));

  const updateProject = useUpdateProject(project.id);
  const archiveProject = useArchiveProject(project.id);
  const [error, setError] = useState('');
  const [editingDescription, setEditingDescription] = useState(false);
  const [descriptionDraft, setDescriptionDraft] = useState('');
  const [editingCategory, setEditingCategory] = useState(false);
  const [categoryDraft, setCategoryDraft] = useState('');
  const [confirmingArchive, setConfirmingArchive] = useState(false);
  const isManager = canManage;

  function report(cause: unknown) {
    setError(
      cause instanceof Error && cause.message === 'project_not_permitted'
        ? 'You do not have permission to change this project.'
        : 'That change could not be saved. Try again.',
    );
  }

  function changeStatus(next: ProjectWorkflowStatus) {
    setError('');
    updateProject.mutate({ status: next }, { onError: report });
  }

  function saveDescription() {
    setError('');
    updateProject.mutate(
      { description: descriptionDraft.trim() },
      { onSuccess: () => setEditingDescription(false), onError: report },
    );
  }

  function saveCategory(value: string) {
    setError('');
    updateProject.mutate(
      // A typo away from an existing category snaps to it instead of
      // quietly founding a near-duplicate group.
      { category: normalizeCategory(value, categories) },
      { onSuccess: () => setEditingCategory(false), onError: report },
    );
  }

  // A deep link points at a task, so the Tasks tab has to be the one showing.
  const [tab, setTab] = useState<ProjectTab>(deepLinkTaskId != null ? 'tasks' : 'overview');

  /**
   * Whether the discussion column is showing.
   *
   * Open by default, because the whole point of the split is that the
   * conversation is visible while you work rather than a tab away. A deep link
   * to a comment forces it open even if somebody collapsed it last time —
   * arriving from a notification and seeing no comment is the one case where
   * the remembered preference is certainly wrong.
   */
  const [discussionOpen, setDiscussionOpen] = useState(
    () => deepLinkCommentId != null || loadDiscussionOpen(window.localStorage),
  );

  useEffect(() => {
    saveDiscussionOpen(window.localStorage, discussionOpen);
  }, [discussionOpen]);

  return (
    <div className="project-split" data-discussion={discussionOpen ? 'open' : 'closed'}>
      <div className="project-canvas">
        <div className="project-topbar">
          <PageHeader
            title={project.name}
            topbar
            breadcrumb={{ label: 'Projects', to: '/projects' }}
          />
          {/* Copying the project's own link sits in the header because the
              header is the one part of the page that is about the project
              rather than about one of its tabs. The note under it names who
              can open the link, which is the whole safety story: the link
              grants nothing, so the person sending it needs to know before
              they send it, not after. */}
          <CopyLinkButton
            url={projectShareUrl(window.location.origin, project.id)}
            visibility={project.visibility}
            className="project-share-link"
          />
          {/* The toggle lives in the header row rather than on the panel it
              controls: when the panel is closed there is no panel to put it
              on, and a control that moves when you use it is a control people
              stop trusting. */}
          <Button
            variant="ghost"
            size="sm"
            className="project-discussion-toggle"
            aria-expanded={discussionOpen}
            aria-controls="project-discussion"
            onClick={() => setDiscussionOpen((open) => !open)}
          >
            {discussionToggleLabel(discussionOpen)}
          </Button>
        </div>

        <nav className="tracker-tab-nav" aria-label="Project sections">
          {PROJECT_TABS.map((entry) => (
            <button
              key={entry.id}
              type="button"
              className={`tracker-tab-button${entry.id === tab ? ' tracker-tab-button--active' : ''}`}
              onClick={() => setTab(entry.id)}
            >
              {entry.label}
            </button>
          ))}
        </nav>

        {tab === 'overview' ? (
          <div className="project-overview-stack">
            <Panel title="Overview">
              {error === '' ? null : <InlineError>{error}</InlineError>}
              {/* The project's facts as grouped rows — Kyu circled this strip
                on 2 September and said "this too". Every control inside is
                the one that was there before, wired the same way; only what
                wraps them changed, from a wrapping <dl> of five columns to one
                card of five rows.

                Then, on 3 September, the second half of that note: as ONE
                full-width card the rows put the label hard left and the
                control hard right with ~700px of nothing between them
                (measured at a 1680px window, discussion open). The grouped-row
                pattern is borrowed from a phone's settings screen and it
                assumes a phone's column width; stretched to a desk monitor it
                stops reading as a list. So the card is now half the width and
                Members sits in the space it gave up — which also lifts Members
                out of the slab it used to occupy below the attachments. */}
              <div className="project-overview-top">
                <RowGroup title="Details">
                  <Row
                    icon="tasks"
                    label="Status"
                    control={
                      <>
                        {canEdit ? (
                          <Select
                            label="Project status"
                            labelHidden
                            value={project.status}
                            options={(
                              Object.keys(PROJECT_WORKFLOW_LABELS) as ProjectWorkflowStatus[]
                            ).map((key) => ({ value: key, label: PROJECT_WORKFLOW_LABELS[key] }))}
                            onChange={(event) =>
                              changeStatus(event.target.value as ProjectWorkflowStatus)
                            }
                          />
                        ) : (
                          <Badge tone={PROJECT_WORKFLOW_TONES[project.status]}>
                            {PROJECT_WORKFLOW_LABELS[project.status]}
                          </Badge>
                        )}
                      </>
                    }
                  />
                  <Row
                    icon="filter"
                    label="Category"
                    control={
                      <>
                        {editingCategory ? (
                          /* Typing a NEW category. The picker that used to sit
                           beside this box has gone: the row itself is now that
                           picker, so having a second one here was offering the
                           same choice twice. */
                          <span className="review-actions">
                            <Input
                              label="New category"
                              labelHidden
                              autoFocus
                              value={categoryDraft}
                              placeholder="Social Media, Corp Gift…"
                              onChange={(event) => setCategoryDraft(event.target.value)}
                            />
                            <Button
                              size="sm"
                              onClick={() => saveCategory(categoryDraft)}
                              disabled={updateProject.isPending}
                            >
                              {updateProject.isPending ? 'Saving…' : 'Save'}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setEditingCategory(false)}
                            >
                              Cancel
                            </Button>
                          </span>
                        ) : canEdit ? (
                          /* A badge and the bare word "Change" — measured, its
                           control started 83px from where the dropdowns above
                           and below it started. Same choice, one control, and
                           the last option opens the box above for a name that
                           does not exist yet. */
                          <Select
                            label="Project category"
                            labelHidden
                            value={project.category.trim() === '' ? '' : project.category}
                            options={[
                              { value: '', label: 'None' },
                              ...categoryOptions.map((category) => ({
                                value: category,
                                label: category,
                              })),
                              { value: NEW_CATEGORY, label: 'New category…' },
                            ]}
                            onChange={(event) => {
                              if (event.target.value === NEW_CATEGORY) {
                                setCategoryDraft('');
                                setEditingCategory(true);
                                return;
                              }
                              saveCategory(event.target.value);
                            }}
                          />
                        ) : project.category.trim() === '' ? (
                          <span className="task-panel-text">None</span>
                        ) : (
                          <Badge tone="neutral">{project.category}</Badge>
                        )}
                      </>
                    }
                  />
                  <Row
                    icon="profile"
                    label="Visibility"
                    control={
                      <>
                        {canManage ? (
                          /* Changeable from here since 3 September, on Kyu's
                           answer: "managers can change it". updateProject has
                           always accepted the column; only the create dialog
                           ever offered it, so a project set Members only at
                           birth could never be opened up without a developer.

                           Managers only — canManage is a TAP Admin or the
                           project's owner. Everyone else still sees the badge,
                           and can_manage_project() in the database refuses
                           anything the caller may not do whatever this says. */
                          <Select
                            label="Project visibility"
                            labelHidden
                            value={project.visibility}
                            options={(Object.keys(VISIBILITY_LABELS) as ProjectVisibility[]).map(
                              (key) => ({ value: key, label: VISIBILITY_LABELS[key] }),
                            )}
                            onChange={(event) => {
                              setError('');
                              updateProject.mutate(
                                { visibility: event.target.value as ProjectVisibility },
                                { onError: report },
                              );
                            }}
                          />
                        ) : (
                          <Badge tone={project.visibility === 'all_tap' ? 'brand' : 'neutral'}>
                            {VISIBILITY_LABELS[project.visibility]}
                          </Badge>
                        )}
                      </>
                    }
                  />
                  <Row
                    icon="calendar"
                    label="Due date"
                    control={
                      <>
                        {canEdit ? (
                          <Input
                            label="Project due date"
                            labelHidden
                            type="date"
                            value={project.due_date ?? ''}
                            onChange={(event) => {
                              setError('');
                              updateProject.mutate(
                                { due_date: event.target.value === '' ? null : event.target.value },
                                { onError: report },
                              );
                            }}
                          />
                        ) : (
                          <span className="task-panel-text">
                            {project.due_date ? formatDate(project.due_date) : 'None'}
                          </span>
                        )}
                      </>
                    }
                  />
                  <Row
                    icon="profile"
                    label="Project owner"
                    control={
                      <>
                        {canManage ? (
                          <Select
                            label="Project owner"
                            labelHidden
                            value={project.owner_id}
                            disabled={handOver.isPending}
                            options={ownerOptions}
                            onChange={(event) => {
                              setOwnerError('');
                              handOver.mutate(event.target.value, {
                                onError: (cause: Error) => {
                                  setOwnerError(handOverErrorMessage(cause.message));
                                },
                              });
                            }}
                          />
                        ) : owner ? (
                          memberLabel(owner)
                        ) : (
                          'Unassigned'
                        )}
                        {ownerError === '' ? null : <InlineError>{ownerError}</InlineError>}
                      </>
                    }
                  />
                </RowGroup>
                <MembersPanel projectId={project.id} members={members} canManage={canManage} />
              </div>

              {/* Description, in its own container.
              The Edit button sits in this header row rather than under the
              text: under a long description it drifts half a page from the
              thing it edits, and under an EMPTY one it lands directly beneath
              the heading looking like it belongs to the next section. */}
              <section className="project-card">
                <header className="project-card-head">
                  <h3 className="project-card-title">Description</h3>
                  {canEdit && !editingDescription ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        setDescriptionDraft(project.description ?? '');
                        setEditingDescription(true);
                      }}
                    >
                      {project.description ? 'Edit description' : 'Add a description'}
                    </Button>
                  ) : null}
                </header>
                {editingDescription ? (
                  <div className="review-composer">
                    <RichTextEditor
                      label="Project description"
                      value={descriptionDraft}
                      onChange={setDescriptionDraft}
                    />
                    <span className="review-actions">
                      <Button
                        size="sm"
                        onClick={saveDescription}
                        disabled={updateProject.isPending}
                      >
                        {updateProject.isPending ? 'Saving…' : 'Save description'}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditingDescription(false)}
                      >
                        Cancel
                      </Button>
                    </span>
                  </div>
                ) : (
                  <RichTextView value={project.description ?? ''} />
                )}
              </section>

              {/* Attachments, in a container of its own. FilesSection brings its
              own heading, so this one only supplies the box. */}
              <section className="project-card">
                <FilesSection
                  taskId=""
                  projectId={project.id}
                  projectName={project.name}
                  canEdit={canEdit}
                  canManage={canManage}
                  headingClassName="project-card-title"
                />
              </section>
            </Panel>

            {isManager ? (
              /* Still the last thing on the canvas, in a container that looks like
               what it is. It used to sit inside Overview between the attachments
               and the members list, where a destructive action reads as just
               another field. Members moved up into Overview on 3 September; this
               did not move with it. */
              <section className="project-danger-zone">
                <div className="project-danger-copy">
                  <h3 className="project-danger-title">Archive project</h3>
                  <p className="project-danger-text">
                    Archiving hides this project from everyone&rsquo;s lists. Nothing is deleted,
                    and it can be restored later.
                  </p>
                </div>
                <div className="project-danger-actions">
                  {confirmingArchive ? (
                    <>
                      <Button variant="ghost" size="sm" onClick={() => setConfirmingArchive(false)}>
                        Keep it
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        disabled={archiveProject.isPending}
                        onClick={() => {
                          setError('');
                          archiveProject.mutate(undefined, {
                            onSuccess: () => void navigate('/projects'),
                            onError: report,
                          });
                        }}
                      >
                        {archiveProject.isPending ? 'Archiving…' : 'Confirm archive'}
                      </Button>
                    </>
                  ) : (
                    /* Two presses, deliberately. The confirm step is what stops a
                     mis-click from taking a project off everybody's list. */
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setConfirmingArchive(true)}
                    >
                      Archive project
                    </Button>
                  )}
                </div>
              </section>
            ) : null}
          </div>
        ) : null}

        {tab === 'tasks' ? (
          <ListView
            deepLinkTaskId={deepLinkTaskId}
            deepLinkCommentId={deepLinkCommentId}
            projectId={project.id}
            projectName={project.name}
            projectVisibility={project.visibility}
            sections={sections}
            members={members}
            statuses={statuses}
            canEdit={canEdit}
            canManage={canManage}
            adminAssignEveryone={profile?.internal_role === 'tap_admin'}
          />
        ) : null}
      </div>

      {/* The project's own conversation — a column now, not a tab.
          It was a tab, which meant the thread was invisible while anybody was
          actually working in Tasks: the place where a question about a task is
          most likely to be asked and least likely to be seen. A mention still
          notifies the named person (Inbox + Slack) through the same pipeline
          as task comments. */}
      {discussionOpen ? (
        <aside
          id="project-discussion"
          className="project-discussion"
          aria-label="Project discussion"
        >
          <div className="project-discussion-head">
            <h2 className="project-discussion-title">Discussion</h2>
            <Button
              variant="ghost"
              size="sm"
              /* A different name from the header toggle on purpose: two
                 controls announcing "Hide discussion" are two controls a
                 screen-reader user cannot tell apart. */
              aria-label="Close discussion"
              onClick={() => setDiscussionOpen(false)}
            >
              &times;
            </Button>
          </div>
          <div className="project-discussion-body">
            <CommentsSection
              taskId={null}
              projectId={project.id}
              canComment={canEdit}
              canManage={canManage}
              members={members}
              highlightCommentId={deepLinkCommentId}
              heading=""
            />
          </div>
        </aside>
      ) : null}
    </div>
  );
}

/**
 * Authorization is resolved by this page's own request, never by a project
 * list loaded earlier. While it resolves, no project detail is rendered.
 */
export function ProjectPage() {
  const [searchParams] = useSearchParams();
  const deepLinkTaskId = searchParams.get('task');
  const deepLinkCommentId = searchParams.get('comment');
  /*
   * The link is NOT cleared here. This component renders a loading skeleton
   * first, so an effect at this level fires while the project query is still
   * in flight — before ProjectWorkspaceView has mounted and read the values.
   * The view's useState initializers then saw null, and a notification link
   * landed on the Overview tab with the discussion collapsed and no comment
   * highlighted. Clearing happens in the view, where the link is consumed.
   */
  const { projectId } = useParams();
  const { access } = useAuth();
  const workspace = useProjectWorkspace(projectId);
  const profile = access.status === 'approved' ? access.profile : null;

  if (workspace.isPending) {
    return (
      <>
        <PageHeader title="Project" topbar breadcrumb={{ label: 'Projects', to: '/projects' }} />
        <Skeleton lines={4} label="Loading project" />
      </>
    );
  }

  if (workspace.isError) {
    return (
      <>
        <PageHeader title="Project" topbar breadcrumb={{ label: 'Projects', to: '/projects' }} />
        <EmptyState
          title="This project could not be loaded"
          description="Something went wrong. Try again in a moment."
        />
      </>
    );
  }

  // No visible row means the caller is not authorized for it, or it does not
  // exist. The two are deliberately indistinguishable so nothing leaks.
  if (!workspace.data) {
    return (
      <>
        <PageHeader title="Project" topbar breadcrumb={{ label: 'Projects', to: '/projects' }} />
        <EmptyState
          title="You do not have access to this project"
          description="Ask the project owner or a TAP admin for access."
        />
      </>
    );
  }

  // A room (channel, DM or group) is a project row, but it lives on the
  // Messages page: no board, no members panel, no hand-over (refused on a DM
  // by design, findings-dev4-drop3b1-db.md item 9). An Inbox mention in a room
  // points here (notificationHref builds /projects/<id>), so send it on.
  if (workspace.data.project.is_room) {
    return (
      <Navigate to={`/messages?room=${encodeURIComponent(workspace.data.project.id)}`} replace />
    );
  }

  return (
    <ProjectWorkspaceView
      workspace={workspace.data}
      profile={profile}
      deepLinkTaskId={deepLinkTaskId}
      deepLinkCommentId={deepLinkCommentId}
    />
  );
}
